/**
 * Static arXiv category map for instant category expansion
 * Eliminates database queries during filtering
 * 
 * Source: https://arxiv.org/category_taxonomy
 */

export const ARXIV_SUBCATEGORIES: Record<string, string[]> = {
  // Computer Science
  cs: [
    'Computer Science', 'cs',
    'cs.AI', 'cs.AR', 'cs.CC', 'cs.CE', 'cs.CG', 'cs.CL', 'cs.CR', 'cs.CV',
    'cs.CY', 'cs.DB', 'cs.DC', 'cs.DL', 'cs.DM', 'cs.DS', 'cs.ET', 'cs.FL',
    'cs.GL', 'cs.GR', 'cs.GT', 'cs.HC', 'cs.IR', 'cs.IT', 'cs.LG', 'cs.LO',
    'cs.MA', 'cs.MM', 'cs.MS', 'cs.NA', 'cs.NE', 'cs.NI', 'cs.OH', 'cs.OS',
    'cs.PF', 'cs.PL', 'cs.RO', 'cs.SC', 'cs.SD', 'cs.SE', 'cs.SI', 'cs.SY'
  ],
  
  // Mathematics
  math: [
    'Mathematics', 'math',
    'math.AC', 'math.AG', 'math.AP', 'math.AT', 'math.CA', 'math.CO',
    'math.CT', 'math.CV', 'math.DG', 'math.DS', 'math.FA', 'math.GM',
    'math.GN', 'math.GR', 'math.GT', 'math.HO', 'math.IT', 'math.KT',
    'math.LO', 'math.MG', 'math.MP', 'math.NA', 'math.NT', 'math.OA',
    'math.OC', 'math.PR', 'math.QA', 'math.RA', 'math.RT', 'math.SG',
    'math.SP', 'math.ST'
  ],
  
  // Physics (includes both physics.* and standalone categories)
  physics: [
    'Physics', 'physics',
    // physics.* subcategories
    'physics.acc-ph', 'physics.ao-ph', 'physics.app-ph', 'physics.atm-clus',
    'physics.atom-ph', 'physics.bio-ph', 'physics.chem-ph', 'physics.class-ph',
    'physics.comp-ph', 'physics.data-an', 'physics.ed-ph', 'physics.flu-dyn',
    'physics.gen-ph', 'physics.geo-ph', 'physics.hist-ph', 'physics.ins-det',
    'physics.med-ph', 'physics.optics', 'physics.plasm-ph', 'physics.pop-ph',
    'physics.soc-ph', 'physics.space-ph',
    // Standalone physics categories
    'astro-ph', 'astro-ph.CO', 'astro-ph.EP', 'astro-ph.GA', 'astro-ph.HE',
    'astro-ph.IM', 'astro-ph.SR',
    'cond-mat', 'cond-mat.dis-nn', 'cond-mat.mes-hall', 'cond-mat.mtrl-sci',
    'cond-mat.other', 'cond-mat.quant-gas', 'cond-mat.soft', 'cond-mat.stat-mech',
    'cond-mat.str-el', 'cond-mat.supr-con',
    'gr-qc', 'hep-ex', 'hep-lat', 'hep-ph', 'hep-th',
    'math-ph', 'nlin', 'nlin.AO', 'nlin.CD', 'nlin.CG', 'nlin.PS', 'nlin.SI',
    'nucl-ex', 'nucl-th', 'quant-ph'
  ],
  
  // Economics
  econ: [
    'Economics', 'econ',
    'econ.EM', 'econ.GN', 'econ.TH'
  ],
  
  // Electrical Engineering and Systems Science
  eess: [
    'Electrical Engineering and Systems Science', 'eess',
    'eess.AS', 'eess.IV', 'eess.SP', 'eess.SY'
  ],
  
  // Quantitative Biology
  'q-bio': [
    'Quantitative Biology', 'q-bio',
    'q-bio.BM', 'q-bio.CB', 'q-bio.GN', 'q-bio.MN', 'q-bio.NC',
    'q-bio.OT', 'q-bio.PE', 'q-bio.QM', 'q-bio.SC', 'q-bio.TO'
  ],
  
  // Quantitative Finance
  'q-fin': [
    'Quantitative Finance', 'q-fin',
    'q-fin.CP', 'q-fin.EC', 'q-fin.GN', 'q-fin.MF', 'q-fin.PM',
    'q-fin.PR', 'q-fin.RM', 'q-fin.ST', 'q-fin.TR'
  ],
  
  // Statistics
  stat: [
    'Statistics', 'stat',
    'stat.AP', 'stat.CO', 'stat.ME', 'stat.ML', 'stat.OT', 'stat.TH'
  ]
};

// Standalone physics categories that don't follow the prefix pattern
export const STANDALONE_PHYSICS_CATEGORIES = [
  'astro-ph', 'cond-mat', 'gr-qc', 'hep-ex', 'hep-lat',
  'hep-ph', 'hep-th', 'math-ph', 'nlin', 'nucl-ex', 'nucl-th', 'quant-ph'
];

/**
 * Expands parent category codes to include all their subcategories
 * Zero database queries - pure in-memory operation
 * 
 * @param categories Array of category codes (e.g., ['cs', 'physics'])
 * @returns Expanded array of all subcategories
 */
export function expandCategories(categories: string[]): string[] {
  if (!categories || categories.length === 0) return [];
  
  const expandedCategories: string[] = [];
  
  for (const category of categories) {
    // Check if it's already a specific subcategory (contains '.' or is a standalone physics category)
    if (category.includes('.') || STANDALONE_PHYSICS_CATEGORIES.includes(category)) {
      expandedCategories.push(category);
    } else if (ARXIV_SUBCATEGORIES[category]) {
      // It's a parent category, add all subcategories from the static map
      expandedCategories.push(...ARXIV_SUBCATEGORIES[category]);
    } else {
      // Unknown category, pass through as-is
      expandedCategories.push(category);
    }
  }
  
  // Remove duplicates
  return [...new Set(expandedCategories)];
}
